import React, {useState} from "react";
import {Modal} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {useOptions} from "hooks/useOptions";
import {TICKET_CATEGORIES, TICKET_OPTIONS_GROUP_NAME} from "constants/ticket";
import {TicketTypeFilters} from "./TicketTypeFilters";
import {TicketTypesTable} from "./TicketTypesTable";
import {TicketTypeModal} from "./TicketTypeModal";

const defaultFilters = {name: "", category: TICKET_CATEGORIES[0], topic: "", skip: 0, limit: 20};

export const TicketTypesPage = () => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const {options} = useOptions({groupName: TICKET_OPTIONS_GROUP_NAME});

    const [filters, setFilters] = useState(defaultFilters);
    const [modalVisible, setModalVisible] = useState(false);
    const [editData, setEditData] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    const [loading, ticketTypes, , , refetch] = useFetch({
        fetchFn: stores.ticketTypeStore.getTicketTypes.bind(null, {...filters}),
        defaultDataValue: {},
        dependencies: [JSON.stringify(filters)]
    });

    const handleFiltersChange = changes => setFilters({...filters, ...changes, skip: 0});

    const handlePageChange = (page, pageSize) =>
        setFilters({...filters, skip: (page - 1) * pageSize, limit: pageSize});

    const openModal = record => {
        setEditData(record);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditData(null);
    };

    const handleModalSuccess = async () => {
        closeModal();
        await refetch();
    };

    const handleToggleActive = async (record, isActive) => {
        setTogglingId(record._id);
        const result = await stores.ticketTypeStore.updateTicketType(record._id, {
            category: record.category,
            topic: record.topic,
            name: record.name,
            priority: record.priority,
            autoAssignRole: record.autoAssignRole,
            isPhotoRequired: record.isPhotoRequired,
            isActive
        });
        setTogglingId(null);
        if (result) {
            await refetch();
        }
    };

    const handleDelete = record => {
        Modal.confirm({
            title: t("ticketTypes.deleteConfirm.title", {ns: "tickets"}),
            content: t("ticketTypes.deleteConfirm.content", {ns: "tickets", name: record.name}),
            okText: t("ticketTypes.deleteConfirm.ok", {ns: "tickets"}),
            okType: "danger",
            cancelText: t("ticketTypes.deleteConfirm.cancel", {ns: "tickets"}),
            onOk: async () => {
                const result = await stores.ticketTypeStore.deleteTicketType(record._id);
                if (result) {
                    await refetch();
                }
            }
        });
    };

    return (
        <div style={{padding: "24px"}}>
            <h2>{t("ticketTypes.title", {ns: "tickets"})}</h2>
            <TicketTypeFilters
                filters={filters}
                options={options}
                onChange={handleFiltersChange}
                onAdd={() => openModal(null)}
            />
            <TicketTypesTable
                rows={ticketTypes?.data?.rows || []}
                total={ticketTypes?.data?.dataCount || 0}
                loading={loading}
                options={options}
                filters={filters}
                togglingId={togglingId}
                onPageChange={handlePageChange}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
            />
            <TicketTypeModal
                visible={modalVisible}
                editData={editData}
                options={options}
                onCancel={closeModal}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};
